import axios from 'axios';
import { getApiDebugLogger } from '../hooks/useApiDebug';

// Create axios instance with base configuration
const customPort = 8000;
const newOriginWithPort = window.location.protocol + "//" + window.location.hostname + ":" + customPort;

const SESSION_STORAGE_KEY = 'doctriage_session_token';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || newOriginWithPort,
  timeout: 300000, // 5 minutes for long-running operations (OCR of large PDFs)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Get API debug logger function
const logApiCall = getApiDebugLogger();

// Request interceptor for debugging and logging
api.interceptors.request.use(
  (config) => {
    // Add timestamp to track request duration
    config.metadata = { startTime: Date.now() };

    // Attach session token from sessionStorage
    const token = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (token) {
      config.headers['X-API-Key'] = token;
    }

    console.log('API Request:', config.method?.toUpperCase(), config.url);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and logging
api.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.status, response.config.url);

    // Calculate request duration
    const duration = response.config.metadata?.startTime
      ? Date.now() - response.config.metadata.startTime
      : null;

    // Extract request details
    const method = response.config.method?.toUpperCase();
    const fullUrl = response.config.baseURL + response.config.url;

    // Parse query params from URL
    let queryParams = {};
    try {
      const url = new URL(fullUrl);
      queryParams = Object.fromEntries(url.searchParams);
    } catch (e) {
      // URL parsing failed, skip query params
    }

    // Get POST data (skip for FormData - too large to display nicely)
    let postData = null;
    if (method === 'POST' || method === 'PUT' || method === 'PATCH') {
      if (response.config.data && !(response.config.data instanceof FormData)) {
        try {
          postData = typeof response.config.data === 'string'
            ? JSON.parse(response.config.data)
            : response.config.data;
        } catch (e) {
          postData = response.config.data;
        }
      } else if (response.config.data instanceof FormData) {
        postData = '[FormData - File Upload]';
      }
    }

    // Get response data (limit size for blobs/large responses)
    let responseData = response.data;
    if (response.data instanceof Blob) {
      responseData = `[Blob - ${response.data.type || 'unknown'} - ${response.data.size} bytes]`;
    }

    // Log to API debug panel
    logApiCall({
      method: method,
      endpoint: response.config.url,
      fullUrl: fullUrl,
      queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
      postData: postData,
      status: response.status,
      response: responseData,
      duration: duration,
    });

    return response;
  },
  (error) => {
    console.error('API Response Error:', error.response?.status, error.message);

    // Calculate request duration
    const duration = error.config?.metadata?.startTime
      ? Date.now() - error.config.metadata.startTime
      : null;

    // Extract request details
    const method = error.config?.method?.toUpperCase() || 'UNKNOWN';
    const fullUrl = error.config?.baseURL + error.config?.url;

    // Parse query params
    let queryParams = {};
    try {
      if (fullUrl) {
        const url = new URL(fullUrl);
        queryParams = Object.fromEntries(url.searchParams);
      }
    } catch (e) {
      // Skip
    }

    // Get POST data
    let postData = null;
    if ((method === 'POST' || method === 'PUT' || method === 'PATCH') && error.config?.data) {
      if (!(error.config.data instanceof FormData)) {
        try {
          postData = typeof error.config.data === 'string'
            ? JSON.parse(error.config.data)
            : error.config.data;
        } catch (e) {
          postData = error.config.data;
        }
      } else {
        postData = '[FormData - File Upload]';
      }
    }

    // Log error to API debug panel
    logApiCall({
      method: method,
      endpoint: error.config?.url || 'Unknown endpoint',
      fullUrl: fullUrl || 'Unknown URL',
      queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
      postData: postData,
      status: error.response?.status || 0,
      response: error.response?.data || null,
      error: error.message || 'Network error',
      duration: duration,
    });

    // On 401 Unauthorized, clear session and force re-login
    if (error.response?.status === 401) {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      window.location.reload();
    }

    return Promise.reject(error);
  }
);

// API service functions
export const apiService = {
  // Health check
  async checkHealth() {
    const response = await api.get('/health');
    return response.data;
  },

  // Client management
  async getClients() {
    const response = await api.get('/clients');
    return response.data;
  },

  async searchClients(query, limit = 20) {
    const response = await api.get('/clients/autocomplete', {
      params: { query, limit }
    });
    return response.data;
  },

  // Document management
  async getClientDocuments(clientId) {
    const response = await api.get(`/clients/${clientId}/documents`);
    return response.data;
  },

  async uploadDocument(clientId, file, metadata = {}, onProgress = null, sourceTimestamp = null) {
    const formData = new FormData();
    formData.append('client_id', clientId);
    formData.append('file', file);
    formData.append('metadata', JSON.stringify(metadata));

    // Add source_timestamp if provided (RFC 3339 format)
    if (sourceTimestamp) {
      formData.append('source_timestamp', sourceTimestamp);
    }

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentCompleted);
      };
    }

    const response = await api.post('/ingest', formData, config);
    return response.data;
  },

  async getJobStatus(jobId) {
    const response = await api.get(`/status/${jobId}`);
    return response.data;
  },

  async deleteDocument(clientId, documentId) {
    const response = await api.delete(`/clients/${clientId}/documents/${documentId}`);
    return response.data;
  },

  async getDocumentPdf(clientId, documentId) {
    // Returns JSON with presigned_url instead of blob
    const response = await api.get(`/clients/${clientId}/documents/${documentId}/pdf`);
    return response.data;
  },

  async getDocumentText(clientId, documentId) {
    const response = await api.get(`/clients/${clientId}/documents/${documentId}/text`);
    return response.data;
  },

  async ingestText(clientId, text, sourceTable, sourceId, sourceTimestamp = null, metadata = {}) {
    const requestData = {
      client_id: clientId,
      text: text,
      source_table: sourceTable,
      source_id: sourceId,
      metadata: metadata
    };

    // Add source_timestamp if provided
    if (sourceTimestamp) {
      requestData.source_timestamp = sourceTimestamp;
    }

    const response = await api.post('/ingest/text', requestData);
    return response.data;
  },

  // Q&A functionality
  async queryDocuments(clientId, query, options = {}) {
    const requestData = {
      client_id: clientId,
      query: query,
      top_k: options.topK || 50,  // Increased from 5 to 50 to catch all relevant documents
      enable_multi_chunk: options.enableMultiChunk !== false,
    };

    // Add timestamp filtering if provided
    if (options.timestampFrom) {
      requestData.timestamp_from = options.timestampFrom;
    }
    if (options.timestampTo) {
      requestData.timestamp_to = options.timestampTo;
    }

    // Add LLM generation parameters if provided (for structured output like JSON)
    if (options.maxTokens !== undefined) {
      requestData.max_tokens = options.maxTokens;
    }
    if (options.temperature !== undefined) {
      requestData.temperature = options.temperature;
    }
    if (options.repetitionPenalty !== undefined) {
      requestData.repetition_penalty = options.repetitionPenalty;
    }
    if (options.frequencyPenalty !== undefined) {
      requestData.frequency_penalty = options.frequencyPenalty;
    }
    if (options.topP !== undefined) {
      requestData.top_p = options.topP;
    }

    const response = await api.post('/query', requestData);
    return response.data;
  },

  // System information
  async getSystemStats() {
    const response = await api.get('/system/stats');
    return response.data;
  },

  async getVllmStatus() {
    const response = await api.get('/vllm/status');
    return response.data;
  },
};

// Helper functions for common operations
export const apiHelpers = {
  // Poll job status until completion
  // Default: 3 seconds × 600 attempts = 30 minutes timeout
  async pollJobStatus(jobId, intervalMs = 3000, maxAttempts = 600) {
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const status = await apiService.getJobStatus(jobId);

        if (status.status === 'completed' || status.status === 'failed') {
          return status;
        }

        // Wait before next poll
        await new Promise(resolve => setTimeout(resolve, intervalMs));
        attempts++;
      } catch (error) {
        console.error('Error polling job status:', error);
        attempts++;

        if (attempts >= maxAttempts) {
          throw new Error('Max polling attempts reached');
        }

        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, intervalMs));
      }
    }

    throw new Error('Job status polling timeout');
  },

  // Upload document and wait for completion
  async uploadAndWaitForCompletion(clientId, file, metadata = {}, onProgress = null, onStatusUpdate = null, sourceTimestamp = null) {
    // Start upload
    const uploadResult = await apiService.uploadDocument(clientId, file, metadata, onProgress, sourceTimestamp);

    if (!uploadResult.success) {
      throw new Error(uploadResult.error || 'Upload failed');
    }

    // Poll for completion with extended timeout for large PDFs
    // 3 seconds × 600 attempts = 30 minutes maximum
    const finalStatus = await this.pollJobStatus(
      uploadResult.job_id,
      3000,
      600
    );

    // Call status update callback if provided
    if (onStatusUpdate) {
      onStatusUpdate(finalStatus);
    }

    if (finalStatus.status === 'failed') {
      throw new Error(finalStatus.error || 'Document processing failed');
    }

    return finalStatus;
  },

  // Get presigned URL for PDF viewing (S3 direct access)
  async getPdfPresignedUrl(clientId, documentId) {
    try {
      const response = await apiService.getDocumentPdf(clientId, documentId);
      if (response.success && response.presigned_url) {
        return response.presigned_url;
      }
      console.error('Failed to get presigned URL:', response.error);
      return null;
    } catch (error) {
      console.error('Error getting PDF presigned URL:', error);
      return null;
    }
  },

  // Legacy method for backwards compatibility (now returns presigned URL)
  async createPdfBlobUrl(clientId, documentId) {
    return this.getPdfPresignedUrl(clientId, documentId);
  },

  // Format file size for display
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },

  // Format date for display
  formatDate(dateString) {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleString();
  },

  // Extract page number from source attribution
  extractPageFromSource(sourceDocuments) {
    if (!sourceDocuments || sourceDocuments.length === 0) {
      return null;
    }

    // Look for page information in source documents
    const firstSource = sourceDocuments[0];
    
    // This might need adjustment based on actual API response format
    if (firstSource.page_number !== undefined) {
      return firstSource.page_number;
    }
    
    // Try to extract from document name or other fields
    return null;
  },
};

export default api;
